'use client';
import {
  IMessagePayload,
  useMessageContext,
  useToast,
  ContextUserType,
  IMessagesQueryParams,
  IMessage,
} from '@ui/components';
import { useEffect, useState, useRef } from 'react';
import {
  useFiles,
  usePostHogFeatureFlag,
  COMMUNITY_MAX_FILES_CREATOR,
  COMMUNITY_MAX_FILES_FAN,
} from '@ui/hooks';
import { useCreatorVideoUploadPermission } from '@ui/hooks/use-creator-video-upload-permission';
import { sendGAEvent } from '@next/third-parties/google';
import dynamic from 'next/dynamic';
import { ContentType } from '@ui/constants/context';
import { useInteractionGuard } from '@hooks/index';
import { useReplyRefContext } from '@ui/contexts';
import { X } from 'lucide-react';
import { CreatingContentBanner } from '../common/creating-content-banner';
import { trackCommunityCommentSubmitted } from '@ui/analytics';
import type { IPollDraftServerError } from '../common/message-poll-draft-editor';
import {
  readPollInvalidError,
  toMessagePollInput,
  type IMessagePollDraft,
} from '@polls/message-poll';

const FAN_MAX_FILES_MESSAGE =
  'You can upload up to 4 files here. Upload up to 10 files in the mobile app.';

const RichTextEditor = dynamic(
  () => import('@ui/components/ui/rte-editor/rte-editor').then((mod) => mod.RichTextEditor),
  { ssr: false },
);

interface Props {
  creator_id: string;
  user_id: string;
  contentType: ContentType;
  userType: ContextUserType;
  replyTo?: { id: string; label: string };
  activateToolbarOnFocus: boolean;
  createThreadIfNotExist: boolean;
  thread_id?: string;
  message_id?: string;
  conversation_id?: string;
  queryParam: IMessagesQueryParams;
  main_post_ref: string;
  miscellaneous?: IMessagePayload['miscellaneous'];
  community_details: IMessage['community_details'];
  onDismiss?: () => void;
  creatorData?: { platform_settings?: { enable_video_uploads?: boolean } };
}
export const CommentOrReplyForm = (props: Props) => {
  const { interceptInteraction } = useInteractionGuard();
  const toast = useToast();
  const [loadingType, setLoadingType] = useState<ContentType | null>(null);
  const messageState = useMessageContext(props.userType);
  const isVideoStreamUploadEnabled = usePostHogFeatureFlag('video-stream-upload');
  const { isVideoUploadEnabled } = useCreatorVideoUploadPermission(props.creatorData);
  const maxFiles =
    props.userType === 'creator' ? COMMUNITY_MAX_FILES_CREATOR : COMMUNITY_MAX_FILES_FAN;
  const { files, onSelectFiles, deleteFile, onDiscardFiles, error, reportMaxFilesExceeded } =
    useFiles({
      maxFiles,
      allowVideos: isVideoStreamUploadEnabled && isVideoUploadEnabled ? true : false,
      maxFilesErrorCreatorStyle: props.userType === 'creator',
    });
  const { setExpanded } = useReplyRefContext();
  const [pollDraft, setPollDraft] = useState<IMessagePollDraft | null>(null);
  const [pollServerError, setPollServerError] = useState<IPollDraftServerError | null>(null);
  const [displayError, setDisplayError] = useState<string | null>(null);
  const [errorDismissed, setErrorDismissed] = useState(false);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isFanMaxFilesError =
    props.userType === 'fan' && error != null && error.includes('only add up to 4');

  useEffect(() => {
    if (error) {
      setErrorDismissed(false);
      setDisplayError(isFanMaxFilesError ? FAN_MAX_FILES_MESSAGE : error);
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
      }
      if (!isFanMaxFilesError) {
        errorTimerRef.current = setTimeout(() => {
          setDisplayError(null);
        }, 10000);
      }
    } else {
      setDisplayError(null);
    }

    return () => {
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
      }
    };
  }, [error, isFanMaxFilesError]);

  const onSubmit = async (
    description: string,
    mentions: string[],
    isSaveDraft: boolean,
    clearEditorContent: () => void,
  ) => {
    setLoadingType(props.contentType);
    setPollServerError(null);
    try {
      let createdThread = null;
      if (
        props.createThreadIfNotExist &&
        !props.thread_id &&
        props.message_id &&
        props.conversation_id
      ) {
        // create comment thread
        const threadResponse = await messageState.onCreateMessageThread({
          message_id: props.message_id,
          conversation_id: props.conversation_id,
          queryParams: props.queryParam,
        });
        const createdThreadId =
          threadResponse?.data?.message_thread?.id || threadResponse?.message_thread?.id;

        // RTK Query mutation triggers resolve with an `{ error }` result unless callers use
        // `.unwrap()`. Do not submit a reply without the newly-created child thread: the API
        // rejects that follow-up request and the composer must remain retryable.
        if (threadResponse?.error || !createdThreadId) {
          toast(
            { title: `Error creating ${props.contentType}, please try again` },
            { type: 'error' },
          );
          return;
        }

        createdThread = createdThreadId;
      }

      const creator_main_id =
        props.creator_id !== null ? props.creator_id : messageState.singleMessage?.creator_id;

      const outgoingMentions = [...mentions];
      if (props.replyTo) {
        const autoMentionIndex = outgoingMentions.indexOf(props.replyTo.id);
        if (autoMentionIndex !== -1) outgoingMentions.splice(autoMentionIndex, 1);
      }

      const res = await messageState.onCreateMessage({
        message: {
          // Keep this payload lightweight and build it only on submit
          send_email: false,
          files: { images: files },
          description,
          creator_id: creator_main_id,
          thread_id: createdThread || props.thread_id,
          user_id: props.user_id,
          mentions: outgoingMentions,
          type: props.contentType,
          main_post_ref: props.main_post_ref,
          draft: isSaveDraft,
          miscellaneous: props.miscellaneous,
          community_details: props.community_details,
          ...(pollDraft ? { poll: toMessagePollInput(pollDraft) } : {}),
        },
        onOptimisticUpdate: () => {},
      });

      if (props.message_id) {
        setExpanded(props.message_id, true);
      }

      if (!res?.error) {
        const persistedMessageId = (res as any)?.data?.message?.id || (res as any)?.data?.id;
        if (persistedMessageId) {
          onDiscardFiles();
          clearEditorContent();
          setPollDraft(null);
          setDisplayError(null);
          setErrorDismissed(false);
          if (props.contentType === 'comment') {
            sendGAEvent('event', 'CreateCommentSuccessful', {
              event_label: 'Comment Creation Successful',
              creatorId: props.creator_id,
              postId: props.message_id,
            });
          }
          trackCommunityCommentSubmitted({
            creator_id: props.creator_id,
            user_id: props.user_id,
            user_type: props.userType === 'creator' ? 'creator' : 'fan',
            post_id: props.main_post_ref,
            parent_type: props.contentType === 'reply' ? 'comment' : 'post',
            has_media: files.length > 0,
            has_poll: Boolean(pollDraft),
            has_mentions: outgoingMentions.length > 0,
            surface: props.userType === 'creator' ? 'creator' : 'fan',
          });
          if (props.onDismiss) props.onDismiss();
        }
      } else {
        const pollInvalid = readPollInvalidError(res.error);
        if (pollInvalid) {
          setPollServerError(pollInvalid);
        } else {
          toast(
            { title: `Error creating ${props.contentType}, please try again` },
            { type: 'error' },
          );
        }
      }
    } catch (e) {
      console.error(e);
      const pollInvalid = readPollInvalidError(e);
      if (pollInvalid) {
        setPollServerError(pollInvalid);
        return;
      }
      toast({ title: `Error creating ${props.contentType}, please try again` }, { type: 'error' });

      // Send GA Event for failed comment creation
      if (props.contentType === 'comment') {
        sendGAEvent('event', 'CreateCommentFailed', {
          event_label: 'Comment Creation Failed',
          creatorId: props.creator_id,
          postId: props.message_id,
        });
      }
    } finally {
      setLoadingType(null);
    }
  };

  const onCancelEditor = () => {
    setPollDraft(null);
    setPollServerError(null);
    onDiscardFiles();
    if (errorTimerRef.current) {
      clearTimeout(errorTimerRef.current);
    }
    setDisplayError(null);
    setErrorDismissed(false);
    if (props.onDismiss) props.onDismiss();
  };

  const onClickCommentBox = interceptInteraction(() => {},
  'You are not a fan of this creator. Collect this badge to join.');

  return (
    <div className="flex flex-col gap-1">
      <CreatingContentBanner
        isVisible={
          props.userType === 'creator' && loadingType === props.contentType && files.length > 0
        }
        kind={props.contentType}
      />
      {messageState.isAuthenticated && messageState.isFanOfCreator ? (
        <>
          {displayError && !errorDismissed && (
            <div className="px-4 py-2 bg-red-50 border border-red-200 rounded-md flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-red-600 font-medium">
                  {isFanMaxFilesError ? FAN_MAX_FILES_MESSAGE : displayError}
                </p>
                {props.userType === 'fan' && !isFanMaxFilesError && (
                  <p className="mt-1 text-xs text-red-600">
                    You can upload up to 4 files here. Upload up to 10 files in the mobile app.
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setErrorDismissed(true)}
                className="shrink-0 rounded p-1 text-red-600 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                aria-label="Dismiss">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          <RichTextEditor
            deleteFile={deleteFile}
            onSelectFiles={onSelectFiles}
            onExceedLimit={reportMaxFilesExceeded}
            isSubmitButtonDisabled={loadingType === props.contentType}
            onChange={onSubmit}
            value=""
            userType="creator"
            creatorDisplayName={messageState?.community_details?.creator_display_name}
            currentUser={props.userType}
            files={files}
            exceededFileLimit={files.length >= maxFiles}
            placeholder={`Type your ${props.contentType}...`}
            editorLabel=""
            buttonLabel={props.contentType === 'comment' ? 'Comment' : 'Reply'}
            editorOnly
            maxFiles={maxFiles}
            activateToolbarOnFocus={props.activateToolbarOnFocus}
            isLoading={loadingType === props.contentType}
            showCancelButton={true}
            onCancel={onCancelEditor}
            enableSuggestions
            initialMention={props.replyTo || undefined}
            allowVideos={isVideoStreamUploadEnabled && isVideoUploadEnabled ? true : false}
            enablePoll
            pollDraft={pollDraft}
            onPollDraftChange={(draft) => {
              setPollServerError(null);
              setPollDraft(draft);
            }}
            pollServerError={pollServerError}
          />
        </>
      ) : (
        <div className="bg-input h-fit rounded-md py-3 pl-4" onClick={onClickCommentBox}>
          <p className="text-sm text-gray-400">
            Type your {props.contentType === 'comment' ? 'comment' : 'reply'}...
          </p>
        </div>
      )}
    </div>
  );
};
